import { ErrorHandler, Injectable } from '@angular/core';
import { ErrorTrackingService } from './error-tracking.service';

/** Wraps a thrown value as an `Error` so it can be logged and reported consistently. */
export function toError(error: any): Error {
  if (error instanceof Error) {
    return error;
  }
  if (typeof error === 'string') {
    return new Error(error);
  }
  if (error && typeof error === 'object') {
    return new Error(JSON.stringify(error));
  }
  return new Error('Unknown error: ' + String(error));
}

/**
 * Angular `ErrorHandler` that reports uncaught errors to Bugsnag. Angular catches these errors
 * itself, so they never reach Bugsnag's window.onerror hook. HTTP failures that
 * `ErrorTrackingInterceptor` already reported are skipped. Apps that need more (spotlight-ui's
 * Amplitude event) extend this and override `afterHandled`.
 */
@Injectable()
export class ErrorTrackingErrorHandler implements ErrorHandler {
  constructor(protected readonly errorTracking: ErrorTrackingService) { }

  public handleError(error: unknown): void {
    const errorObj = toError(error);
    console.error(errorObj);
    if (!this.errorTracking.isAlreadyReported(error)) {
      this.errorTracking.notify(errorObj, (event) => {
        event.unhandled = true;
        event.severity = 'error';
      });
    }
    this.afterHandled(errorObj);
  }

  /** Runs for every handled error, including ones not sent to Bugsnag. */
  protected afterHandled(_error: Error): void { }
}
